import React, { useState, useRef } from 'react';
import { Upload, Download, AlertTriangle, Check, X } from 'lucide-react';
import { read, utils, writeFile } from 'xlsx';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { useInventory } from '../../context/InventoryContext';

interface PreviewData {
  products?: any[];
  categories?: any[];
  transactions?: any[];
}

const ExcelImport: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { importData } = useInventory();
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const validateData = (data: any) => {
    if (!data.products || !Array.isArray(data.products)) {
      throw new Error('Invalid products sheet format');
    }
    if (!data.categories || !Array.isArray(data.categories)) {
      throw new Error('Invalid categories sheet format');
    }
    if (!data.transactions || !Array.isArray(data.transactions)) {
      throw new Error('Invalid transactions sheet format');
    }

    // Validate required fields
    data.products.forEach((product: any, index: number) => {
      if (!product.name || !product.sku) {
        throw new Error(`Missing required fields in product at row ${index + 2}`);
      }
    });

    data.categories.forEach((category: any, index: number) => {
      if (!category.name) {
        throw new Error(`Missing required fields in category at row ${index + 2}`);
      }
    });

    data.transactions.forEach((transaction: any, index: number) => {
      if (!transaction.product_id || !transaction.type || !transaction.quantity) {
        throw new Error(`Missing required fields in transaction at row ${index + 2}`);
      }
    });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setError(null);
      const data = await file.arrayBuffer();
      const workbook = read(data);

      const importData = {
        products: utils.sheet_to_json(workbook.Sheets['Products'] || {}),
        categories: utils.sheet_to_json(workbook.Sheets['Categories'] || {}),
        transactions: utils.sheet_to_json(workbook.Sheets['Transactions'] || {}),
      };

      validateData(importData);
      setPreviewData(importData);
    } catch (error) {
      console.error('Import error:', error);
      setError(error instanceof Error ? error.message : 'Failed to parse Excel file');
      setPreviewData(null);
    }

    // Reset file input
    e.target.value = '';
  };

  const handleImport = async () => {
    if (!previewData) return;

    try {
      await importData(previewData);
      setPreviewData(null);
      alert('Data imported successfully!');
    } catch (error) {
      console.error('Import error:', error);
      setError('Failed to import data');
    }
  };

  const generateTemplate = () => {
    // Create workbook
    const wb = utils.book_new();

    // Products sheet
    const productsData = [{
      name: 'Example Product',
      sku: 'PROD001',
      description: 'Product description',
      category_name: 'Example Category', // Changed from category_id to category_name
      price: 99.99,
      cost_price: 50.00,
      stock_quantity: 100,
      min_stock_level: 10,
      image: 'https://example.com/image.jpg',
      location: 'Warehouse A'
    }];
    const ws_products = utils.json_to_sheet(productsData);
    utils.book_append_sheet(wb, ws_products, 'Products');

    // Categories sheet
    const categoriesData = [{
      name: 'Example Category',
      description: 'Category description',
      color: '#3B82F6'
    }];
    const ws_categories = utils.json_to_sheet(categoriesData);
    utils.book_append_sheet(wb, ws_categories, 'Categories');

    // Transactions sheet
    const transactionsData = [{
      product_id: 'PROD001',
      type: 'in',
      quantity: 10,
      notes: 'Initial stock',
      status: 'completed',
      currency: 'USD'
    }];
    const ws_transactions = utils.json_to_sheet(transactionsData);
    utils.book_append_sheet(wb, ws_transactions, 'Transactions');

    // Add column widths
    const wscols = [
      { wch: 20 }, // A
      { wch: 15 }, // B
      { wch: 30 }, // C
      { wch: 15 }, // D
      { wch: 12 }, // E
      { wch: 12 }, // F
      { wch: 12 }, // G
      { wch: 12 }, // H
      { wch: 30 }, // I
      { wch: 20 }  // J
    ];

    ws_products['!cols'] = wscols;
    ws_categories['!cols'] = wscols;
    ws_transactions['!cols'] = wscols;

    // Write the file
    writeFile(wb, 'inventory_template.xlsx', {
      bookType: 'xlsx',
      type: 'binary'
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Excel Data Import</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 border border-gray-200 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Import Data</h3>
            <p className="text-sm text-gray-700 mb-4">
              Import inventory data from an Excel file (.xlsx or .xls)
            </p>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
            />
            <Button variant="secondary" onClick={() => fileInputRef.current?.click()}>
              <Upload className="h-4 w-4 mr-2" />
              Select Excel File
            </Button>
          </div>

          <div className="p-4 border border-gray-200 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Download Template</h3>
            <p className="text-sm text-gray-700 mb-4">
              Get a sample Excel template with the correct format
            </p>
            <Button variant="secondary" onClick={generateTemplate}>
              <Download className="h-4 w-4 mr-2" />
              Download Template
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        )}

        {previewData && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Data Preview</h3>
            
            <div className="space-y-2">
              <div className="flex items-center">
                <Check className="h-4 w-4 text-green-500 mr-2" />
                <span className="text-sm text-gray-600">
                  Products: {previewData.products?.length || 0} items
                </span>
              </div>
              <div className="flex items-center">
                <Check className="h-4 w-4 text-green-500 mr-2" />
                <span className="text-sm text-gray-600">
                  Categories: {previewData.categories?.length || 0} items
                </span>
              </div>
              <div className="flex items-center">
                <Check className="h-4 w-4 text-green-500 mr-2" />
                <span className="text-sm text-gray-600">
                  Transactions: {previewData.transactions?.length || 0} items
                </span>
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <Button variant="secondary" onClick={() => setPreviewData(null)}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleImport}>
                <Check className="h-4 w-4 mr-2" />
                Confirm Import
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ExcelImport;